import { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { runPolicies, UserKeys } from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { pagesConfig } from '../../shared/pages-config';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { Policy } from '../../../types';
import { Messages } from '../../utils/message-constants';

interface UsePolicyActionsOptions {
  onRunSuccess?: (ids: number[]) => void;
}

export function usePolicyActions(options?: UsePolicyActionsOptions) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();

  const [runChangeLogOpen, setRunChangeLogOpen] = useState(false);
  const [runChangeLogMessage, setRunChangeLogMessage] = useState('');
  const pendingRunIdsRef = useRef<number[]>([]);

  const closeDrawer = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('selectedPolicy');
      return prev;
    });
  }, [setSearchParams]);

  const handlePolicyEdit = useCallback(
    (policy: Policy) => {
      navigate({
        pathname: `/${
          pagesConfig.policies.link
        }/${pagesConfig.editPolicy.link.replace(':id', policy.id.toString())}`,
        search: '',
      });
    },
    [navigate]
  );

  const handlePolicyClone = useCallback(
    (policy: Policy) => {
      navigate({
        pathname: `/${
          pagesConfig.policies.link
        }/${pagesConfig.clonePolicy.link.replace(':id', policy.id.toString())}`,
        search: '',
      });
    },
    [navigate]
  );

  const { mutate: runPoliciesMutation, isPending: isRunning } = useMutation({
    mutationFn: ({
      selectedPolicies,
      changeLogMessage,
    }: {
      selectedPolicies: number[];
      changeLogMessage: string;
    }) => runPolicies(selectedPolicies, changeLogMessage),
    onMutate: ({ selectedPolicies: ids }) => {
      const previous = queryClient.getQueryData<Policy[]>([UserKeys.Policies]);
      queryClient.setQueryData<Policy[]>(
        [UserKeys.Policies],
        (old) =>
          old?.map((p) =>
            ids.includes(p.id) ? { ...p, status: 'RUNNING' as const } : p
          )
      );
      return { previous };
    },
    onSuccess: (returnData, { selectedPolicies: ids }) => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          ids.length > 1
            ? 'The selected policies are now running!'
            : 'The policy is now running!',
      });
      queryClient.setQueryData<Policy[] | undefined>(
        [UserKeys.Policies],
        (oldData) => {
          if (!oldData) return;
          return oldData.map((policy) =>
            ids.includes(policy.id)
              ? { ...policy, lastRunDate: returnData }
              : policy
          );
        }
      );
      setRunChangeLogOpen(false);
      setRunChangeLogMessage('');
      options?.onRunSuccess?.(ids);
    },
    onError: (error, { selectedPolicies: ids }, context) => {
      if (context?.previous) {
        queryClient.setQueryData([UserKeys.Policies], context.previous);
      }
      handleErrors({
        error,
        snackbarMessage:
          ids.length > 1
            ? Messages.Errors.Network.Running('the selected policies')
            : Messages.Errors.Network.Running('this policy'),
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.policies.run_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.policies,
      });
    },
  });

  const handleRunClick = useCallback(
    (ids: number[], changeLogRoles: string[]) => {
      if (
        isAbleToSeeUIElement(changeLogRoles, ToolsRoles.MaunalRunPolicy.value)
      ) {
        pendingRunIdsRef.current = ids;
        setRunChangeLogOpen(true);
      } else {
        runPoliciesMutation({ selectedPolicies: ids, changeLogMessage: '' });
      }
    },
    [runPoliciesMutation]
  );

  const handleRunWithChangeLog = useCallback(() => {
    runPoliciesMutation({
      selectedPolicies: pendingRunIdsRef.current,
      changeLogMessage: runChangeLogMessage,
    });
  }, [runPoliciesMutation, runChangeLogMessage]);

  return {
    closeDrawer,
    handlePolicyEdit,
    handlePolicyClone,
    isRunning,
    runChangeLogOpen,
    setRunChangeLogOpen,
    runChangeLogMessage,
    setRunChangeLogMessage,
    handleRunClick,
    handleRunWithChangeLog,
  };
}
